import type {
  ConfigGameRequest,
  CreateGameRequest,
  GameDetailResponse,
  GameReviewResponse,
  GuessStateResponse,
  KickRequest,
  LeaderboardResponse,
  ListGamesResponse,
  ListMessagesResponse,
  Me,
  MessageResponse,
  MeResponse,
  RoundOverviewResponse,
  RoundViewResponse,
  SetProductRequest,
  SignupRequest,
  SignupResponse,
  StartRoundRequest,
  SubmitGuessRequest,
  TagEventRequest,
} from "backend/types";

const TOKEN_KEY = "dukaan.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new Error((data && data.error) || `${method} ${path} failed: ${res.status}`);
  }
  return data as T;
}

export const api = {
  signup: (b: SignupRequest) => req<SignupResponse>("POST", "/api/auth/signup", b),
  me: () => req<MeResponse>("GET", "/api/auth/me"),

  listGames: () => req<ListGamesResponse>("GET", "/api/games"),
  createGame: (b: CreateGameRequest) => req<GameDetailResponse>("POST", "/api/games", b),
  getGame: (id: string) => req<GameDetailResponse>("GET", `/api/games/${id}`),
  joinGame: (id: string) => req<GameDetailResponse>("POST", `/api/games/${id}/join`),
  setProduct: (id: string, b: SetProductRequest) =>
    req<GameDetailResponse>("POST", `/api/games/${id}/product`, b),
  configGame: (id: string, b: ConfigGameRequest) =>
    req<GameDetailResponse>("POST", `/api/games/${id}/config`, b),
  kick: (id: string, b: KickRequest) => req<GameDetailResponse>("POST", `/api/games/${id}/kick`, b),
  startRound: (id: string, b: StartRoundRequest) =>
    req<GameDetailResponse>("POST", `/api/games/${id}/rounds`, b),
  endGame: (id: string) => req<GameDetailResponse>("POST", `/api/games/${id}/end`),

  getRound: (id: string) => req<RoundViewResponse>("GET", `/api/games/${id}/round`),
  getOverview: (id: string) => req<RoundOverviewResponse>("GET", `/api/games/${id}/overview`),
  endRound: (roundId: string) => req<{ ok: boolean }>("POST", `/api/rounds/${roundId}/end`),

  listMessages: (pairingId: string) =>
    req<ListMessagesResponse>("GET", `/api/pairings/${pairingId}/messages`),
  sendMessage: (pairingId: string, body: string) =>
    req<MessageResponse>("POST", `/api/pairings/${pairingId}/messages`, { body }),
  tagEvent: (pairingId: string, b: TagEventRequest) =>
    req<MessageResponse>("POST", `/api/pairings/${pairingId}/events`, b),

  getGuess: (roundId: string) => req<GuessStateResponse>("GET", `/api/rounds/${roundId}/guess`),
  submitGuess: (roundId: string, b: SubmitGuessRequest) =>
    req<{ correct: boolean; partner: { id: string; name: string; color: string } }>(
      "POST",
      `/api/rounds/${roundId}/guess`,
      b,
    ),
  leaderboard: (id: string) => req<LeaderboardResponse>("GET", `/api/games/${id}/leaderboard`),
  getReview: (id: string) => req<GameReviewResponse>("GET", `/api/games/${id}/review`),
};

export type { Me };
